import {useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {MicroLabel} from '@/components/period-selector'
import {WorkerCountDialog} from '@/components/worker-count-dialog'
import {WorkerCountDivergenceNote} from '@/components/worker-count-divergence-note'
import {useUpgradeGate} from '@/components/billing/use-upgrade-gate'
import {useUpdateBusiness} from '@/hooks/empresa/organizacion/use-businesses'
import {translateApiError} from '@/lib/errors'

/** Con la autoevaluación registrada el conteo del assessment queda congelado: aquí solo se edita
 * `business.declared_worker_count`. */
export function DeclaredWorkerCount({
  businessId,
  declaredCount,
  activeCount,
  fase2,
  editable,
}: {
  businessId: string
  declaredCount: number | null
  activeCount: number
  fase2: boolean
  editable: boolean
}) {
  const {t} = useTranslation()
  const update = useUpdateBusiness(businessId)
  const [open, setOpen] = useState(false)
  const [initial, setInitial] = useState(1)
  const [error, setError] = useState<string | null>(null)
  // Subir el conteo puede cruzar la banda pagada y volver 409 de gate.
  const upgradeGate = useUpgradeGate(businessId, 'conteo', (band) =>
    setError(t('planFacturacion.upgradeModal.retryNote', {band}))
  )

  if (!fase2) return null

  function edit(seed: number) {
    setInitial(Math.max(seed, 1))
    setError(null)
    setOpen(true)
  }

  function save(count: number) {
    update.mutate(
      {declared_worker_count: count},
      {
        onSuccess: () => setOpen(false),
        onError: (err) => {
          if (upgradeGate.catchGate(err, {proposed_declared_worker_count: count})) {
            setOpen(false)
            return
          }
          setError(translateApiError(err))
        },
      }
    )
  }

  return (
    <div className="space-y-3" data-testid="declared-worker-count">
      <div className="surface flex flex-wrap items-center justify-between gap-3 px-4 py-3">
        <div className="min-w-0">
          <MicroLabel>{t('workerCount.declaredLabel')}</MicroLabel>
          <p className="mt-0.5 font-mono text-lg text-foreground" data-testid="declared-value">
            {declaredCount ?? t('workerCount.declaredEmpty')}
          </p>
        </div>
        {editable && (
          <Button
            size="sm"
            variant="outline"
            data-testid="edit-declared"
            onClick={() => edit(declaredCount ?? activeCount)}>
            {t('workerCount.edit')}
          </Button>
        )}
      </div>

      <WorkerCountDivergenceNote
        businessId={businessId}
        declared={declaredCount ?? 0}
        active={activeCount}
        editable={editable}
        onUpdateDeclared={() => edit(activeCount)}
        noticeTestId="declared-nudge"
        updateTestId="nudge-update"
      />

      <WorkerCountDialog
        open={open}
        onOpenChange={setOpen}
        title={t('workerCount.declaredDialogTitle')}
        description={t('workerCount.declaredDialogBody')}
        initial={initial}
        pending={update.isPending}
        error={error}
        onSave={save}
      />
      {upgradeGate.modal}
    </div>
  )
}
